// Utilities
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
// Interfaces
import { BasicAjaxClient } from "../ajaxClients/BasicAjaxClient";
import { CorrelationSettingAjaxClient } from "../ajaxClients/CorrelationSettingAjaxClient";
import { RetryAjaxClient } from "../ajaxClients/RetryAjaxClient";
// Constants
import { RetryAjaxHelperConfigConstants } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { HttpMethods } from "@ms/uno-constants/lib/local/HttpConstants";
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { SharepointScenarioType } from "@ms/uno-constants/lib/local/ServiceConstants";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { Allotments, SelfServiceSession } from "../constants/ProjectSchedulingServiceConstants";
import { AjaxTimeoutError } from "./TransportFailureUtilities";
/**
 * Perform fetch while adding a timeout mechanic on top
 * @param url Resource url for the request
 * @param options Options to configure the request
 * @param globalTimeout Fallback global timeout value in MS
 */ export async function fetchWithTimeout(url, options, globalTimeout) {
    const timeoutMs = options.timeout ?? globalTimeout;
    const controller = new AbortController();
    const callerSignal = options.signal;
    let abortSource;
    const abortFromCaller = ()=>{
        if (abortSource === undefined) {
            abortSource = "caller";
            controller.abort();
        }
    };
    if (callerSignal) {
        if (callerSignal.aborted) {
            abortFromCaller();
        } else {
            callerSignal.addEventListener("abort", abortFromCaller, {
                once: true
            });
        }
    }
    const id = setTimeout(()=>{
        if (abortSource === undefined) {
            abortSource = "timeout";
            controller.abort();
        }
    }, timeoutMs);
    try {
        return await fetch(url, {
            ...options,
            signal: controller.signal
        });
    } catch (error) {
        if (abortSource === "timeout") {
            throw new AjaxTimeoutError(timeoutMs);
        }
        throw error;
    } finally{
        callerSignal?.removeEventListener("abort", abortFromCaller);
        clearTimeout(id);
    }
}
/**
 * Return an error object for a failure API call
 * @param response Response object
 * @param e an error that can be a string or an Error object
 */ export function generateAjaxClientError(response, e) {
    const failureResult = {
        response: response,
        error: typeof e === "string" ? new Error(e) : e ?? null
    };
    return failureResult;
}
/**
 * Determines whether a failed entity operation (update or delete) should be retried after re-fetching
 * the latest server copy. A retry is warranted when the server reported a version conflict (HTTP
 * 409/412), meaning the local baseline can no longer be trusted. Retries are capped by `maxRetries`.
 * @param error The error thrown by the operation, expected to carry an optional `response.status`
 * @param retryCount The number of retries already attempted
 * @param maxRetries The maximum number of retries allowed
 */ export function shouldRetryEntityOperationOnConflict(error, retryCount, maxRetries) {
    const status = error?.response?.status;
    return (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed) && retryCount < maxRetries;
}
/**
 * Attempts to extract the service error code from a failed response body.
 * Expected error response shape:
 * {
 *   "error": {
 *     "code": "ETagVersionRollback",
 *     "message": "ETag format version has been rolled back. Client must resync."
 *   }
 * }
 * @param response The response from the failed request, may be null
 * @returns The error code if present, otherwise null
 */ export async function getErrorCodeFromResponse(response) {
    if (response == null) {
        return null;
    }
    try {
        const parsed = await response.clone().json();
        return parsed.error?.code ?? null;
    } catch  {
        // Body was not JSON or did not match the expected shape; no error code to extract.
        return null;
    }
}
/**
 * Resolves the error code from a failed request response when the Dirty-bit check is enabled and the
 * response status matches the scenario's expected status (HTTP 412 for entity updates, HTTP 410 for
 * delta sync). Failure actions use the error code to detect the ETag version rollback case and mark
 * the store entity Dirty. Returns `undefined` (without reading the body) when the check is disabled or
 * the status does not match.
 * @param configProvider Config provider used to read the `EnableDirtyBitCheck` flight
 * @param expectedStatus The HTTP status the scenario expects for a Dirty-bit rollback (412 for update, 410 for delta sync)
 * @param response The response from the failed request, may be null
 * @returns The error code if available, the check is enabled, and the status matches, otherwise `undefined`
 */ export async function getErrorCodeForDirtyCheck(configProvider, expectedStatus, response) {
    if (!configProvider().flights.EnableDirtyBitCheck || response?.status !== expectedStatus) {
        return undefined;
    }
    return await getErrorCodeFromResponse(response) ?? undefined;
}
/**
 * Generate a log string from a failed request result.
 * We don't want to log error message from the service as it may contain sensitive information.
 * @param failedRequestResult Failed request result
 */ export function generateLogStringFromAjaxClientError(failedRequestResult) {
    const { response, error, extraTelemetryData } = failedRequestResult;
    let extraTelemetryDataString = "";
    if (extraTelemetryData) {
        Object.entries(extraTelemetryData).forEach(([key, value])=>{
            extraTelemetryDataString += `[${key}=${value}]`;
        });
    }
    let errorMessageString = "";
    let typeErrorDiagnosticInfo = "";
    if (AppContext?.appConfiguration?.flights?.EnableErrorMessageLogging && (response?.status == null || response.status < HttpStatus.BadRequest)) {
        errorMessageString = `[ErrorMessage=${ErrorUtilities.getMessage(error) ?? "No error message"}]`;
        // Extra diagnostic info for TypeError investigations
        if (error instanceof TypeError) {
            typeErrorDiagnosticInfo = response ? `[ResponseType=${response.type}][ResponseBodyUsed=${response.bodyUsed}]` : "[NoResponse=true]";
        }
    }
    return `[StatusCode=${response?.status}][StatusText=${response?.statusText}][ErrorType=${error?.name}]${extraTelemetryDataString}${errorMessageString}${typeErrorDiagnosticInfo}`;
}
/**
 * Generate structured extra data from a failed request result for telemetry.
 * This complements generateLogStringFromAjaxClientError by providing queryable
 * fields in Data_Diagnostics_ExtraData_* columns (e.g., http_status_code).
 * @param failedRequestResult Failed request result
 */ export function generateExtraDataFromAjaxClientError(failedRequestResult) {
    const { response, error, extraTelemetryData } = failedRequestResult;
    const result = {};
    // Merge caller-supplied data first so named fields below take precedence
    if (extraTelemetryData) {
        Object.assign(result, extraTelemetryData);
    }
    if (response?.status != null) {
        result["http_status_code"] = String(response.status);
    }
    if (error?.name) {
        result["error_type"] = error.name;
    }
    if (AppContext?.appConfiguration?.flights?.EnableErrorMessageLogging && (response?.status == null || response.status < HttpStatus.BadRequest)) {
        result["error_message"] = ErrorUtilities.getMessage(error) ?? "No error message";
    }
    return result;
}
/**
 * Check the parameter for null values for the given keys. Generate the IFailedRequestResult for any null values.
 * @param parameters the request parameters to validate
 * @param notEmptyKeys (optional) list of keys to test with isEmpty
 */ export function validateAjaxClientRequestParameters(parameters, notEmptyKeys) {
    if (parameters == null) {
        return generateAjaxClientError(new Response("ArgumentNullException: parameters", {
            status: HttpStatus.BadRequest,
            statusText: "Bad Request"
        }), "ArgumentNullException: parameters");
    }
    if (notEmptyKeys) {
        for (const key of notEmptyKeys){
            if (isEmpty(parameters[key])) {
                const errorText = "ArgumentNullException: parameters." + key;
                return generateAjaxClientError(new Response(errorText, {
                    status: HttpStatus.BadRequest,
                    statusText: "Bad Request"
                }), errorText);
            }
        }
    }
    return null;
}
/**
 * Check if a failed request is retryable
 * @param statusCode Request status code
 */ export function isRetryableStatusCode(status) {
    if (status === HttpStatus.BadGateway || status === HttpStatus.ServiceUnavailable || status === HttpStatus.GatewayTimeout || status === 0) {
        // Gateway timeout
        return true;
    }
    return false;
}
/**
 * Return boolean indicating if given response error is a timeout error or not
 * @param response The current response
 */ export function isTimeoutErrorResponse(response) {
    return response.error?.message.toLowerCase() === "timeout"; // Ajax error message that indicates timeout
}
/**
 * Gets the default ajax retry config
 * @param overrides The overrides to apply to the default config
 */ export function getAjaxRetryConfig(overrides) {
    return merge({
        delayOnRetryInMiliseconds: RetryAjaxHelperConfigConstants.delayOnRetryInMiliseconds,
        maxRetryCount: RetryAjaxHelperConfigConstants.maxRetryCount,
        maxRetryTimeoutInMiliseconds: RetryAjaxHelperConfigConstants.maxRetryTimeoutInMiliseconds,
        retryCheck: retryCheckCallbackForAjaxClientRequest
    }, overrides);
}
/**
 * Builds the bottom ajax client for services that require a fresh correlation header per transient retry.
 * Composes as `RetryAjaxClient(CorrelationSettingAjaxClient(BasicAjaxClient))` so every retry attempt
 * re-enters `CorrelationSettingAjaxClient` and stamps a new GUID. Use this anywhere a service needs the
 * "one correlation GUID per attempt" contract — not just Planner.
 */ export function buildRetryAjaxClientWithCorrelation(retrySettings, correlationHeader, loggers) {
    const { globalAjaxTimeout, maxRetryCount, maxRetryTimeout, defaultRetryDelay } = retrySettings;
    const basicAjaxClient = new BasicAjaxClient(globalAjaxTimeout, loggers);
    const correlationAjaxClient = new CorrelationSettingAjaxClient(basicAjaxClient, correlationHeader, loggers);
    return new RetryAjaxClient(correlationAjaxClient, getAjaxRetryConfig({
        maxRetryCount,
        maxRetryTimeoutInMiliseconds: maxRetryTimeout,
        delayOnRetryInMiliseconds: defaultRetryDelay
    }), loggers);
}
/**
 * Callback to check on a failure response if a retry should be attempted or not, returning TRUE will retry the request otherwise retry loop will stop.
 * @param options The request options
 * @param failureResult The failure result of the request
 */ export async function retryCheckCallbackForAjaxClientRequest(options, failureResult) {
    return isGetRequest(options) && isRetryableResponse(failureResult);
}
/**
 * Returns boolean indicating if given response is retryable or not
 * @param response The current response
 */ export async function isRetryableResponse(response) {
    const statusCode = response?.response?.status ?? 0;
    return isRetryableStatusCode(statusCode) || // One of retryable status code
    isTimeoutErrorResponse(response);
}
/**
 * Check if the request method is GET
 * @param options Request option
 */ export function isGetRequest(options) {
    return options.method == null || options.method.toUpperCase() === HttpMethods.Get;
}
/**
 * Get the headers for a project api call, if current session is a self-service session, add force strong consistency header
 * @param headers headers of a project api call
 * @param isSelfService whether current session is a self-service session
 */ export function getProjectApiCallHeader(headers, isSelfService) {
    if (isSelfService) {
        headers[SelfServiceSession] = Allotments;
        return headers;
    } else {
        return headers;
    }
}
/**
 * Get the header value from the failed request result response if it exists
 * @param failedRequestResult Failed request result
 * @param headerName The name of the response header to get
 */ export function getHeaderFromFromAjaxClientError(failedRequestResult, headerName) {
    return failedRequestResult.response?.headers.get(headerName) ?? null;
}
/**
 * Type guard for IFailedRequestResult
 * @param obj The object to check
 * @returns True if the object is an IFailedRequestResult
 */ export function isFailedRequestResult(obj) {
    return typeof obj === "object" && obj !== null && "response" in obj && (obj.response === null || obj.response instanceof Response) && "error" in obj && (obj.error === null || obj.error instanceof Error) && (obj.retryCount == null || typeof obj.retryCount === "number") && (obj.extraTelemetryData == null || typeof obj.extraTelemetryData === "object");
}
/**
 * Method to get the list of apps that are enabled in the environment.
 * @param environmentFeatureSettings The feature settings for the environment indicating which apps are enabled.
 * @returns {string[]}list of apps that are enabled in the environment.
 */ export function getAppsEnabledInEnvironment(environmentFeatureSettings) {
    const enabledApps = [
        "Planner"
    ];
    const { EnablePortfolioExperience, EnablePremiumPlanExperience, EnableTodoExperience } = environmentFeatureSettings;
    if (EnablePortfolioExperience === undefined || EnablePremiumPlanExperience === undefined || EnableTodoExperience === undefined) {
        return [
            "Planner",
            "Portfolio",
            "Project",
            "ToDo"
        ];
    }
    if (EnablePortfolioExperience) {
        enabledApps.push("Portfolio");
    }
    if (EnablePremiumPlanExperience) {
        enabledApps.push("Project");
    }
    if (EnableTodoExperience) {
        enabledApps.push("ToDo");
    }
    return enabledApps;
}
/**
 * Returns the SharePoint scenario information for a given user scenario.
 * @param userScenario The user scenario for which to get the SharePoint scenario info.
 * @returns An object containing the scenario type and user scenario.
 */ export function getSharepointScenarioInfo(userScenario) {
    return {
        scenarioType: SharepointScenarioType.ActiveUserOperation,
        userScenario
    };
}
